import { Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-light-svg-icons";
import CurrencyFormat from "react-currency-format";
import { useSelector } from "react-redux";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useGetAccountQuery } from "@generated";
import { RootState } from "@reducers/rootReducer";
import { usePendingApprovals } from "./api";
import { PendingApproval } from "./types";
import { useStyles } from "./styles";

const PendingApprovals = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: account } = useGetAccountQuery({ slug: accountSlug });
  const { data, isLoading } = usePendingApprovals();

  const currencyUnit =
    account?.data?.attributes?.["field-labels"]?.["currency_unit"] ?? "$";

  const columns: ColumnDef<PendingApproval>[] = [
    {
      header: "Project Name",
      cell: (props) => (
        <span
          className={styles.projectLink}
          onClick={() => navigate(`/projects/${props.row.original.id}/edit`)}
        >
          {props.row.original.projectName}
        </span>
      ),
    },
    {
      header: "Customer",
      cell: (props) => props.row.original.clientName,
    },
    {
      header: "Revenue",
      cell: (props) => (
        <span className={styles.numericCell}>
          <CurrencyFormat
            displayType="text"
            prefix={currencyUnit}
            isNumericString
            thousandSeparator
            value={props.row.original.contractRevenue.toFixed(2)}
          />
        </span>
      ),
    },
    {
      header: "Margin",
      cell: (props) => (
        <span className={styles.numericCell}>
          {new Intl.NumberFormat("en-US", {
            style: "percent",
            minimumFractionDigits: 2,
          }).format(props.row.original.contractMargin)}
        </span>
      ),
    },
    {
      header: "Time Waiting",
      cell: (props) => props.row.original.timeWaiting,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Row>
        <Col>
          <h1 className={styles.title}>Projects for My Approval</h1>
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon
                  className={styles.cardIcon}
                  icon={faCircleCheck}
                />
                <h2>Pending Approvals</h2>
              </div>
            </div>
            <hr className={styles.line} />
            {isLoading ? (
              <ScopeStackSpinner />
            ) : data.length === 0 ? (
              <div className={styles.emptyState}>
                <h3>You're all caught up!</h3>
                <p>No projects are currently waiting on your approval.</p>
              </div>
            ) : (
              <DataTable data={data} columns={columns} striped />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PendingApprovals;
